import React, { useEffect, useMemo, useState } from 'react';
import { base44 } from '../api/base44Client';
import { buildBlogPostPayload } from '../api/blogPostPayload';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || '';

const initialForm = {
  title: '',
  destination: '',
  excerpt: '',
  content: '',
  image: '',
  author: '',
  category: 'travel',
};

const initialDestinationForm = {
  name: '',
  country: '',
  description: '',
  content: '',
  image: '',
  category: 'city',
  best_season: '',
  highlights: '',
};

const blogCategories = [
  'travel',
  'adventure',
  'food',
  'culture',
  'beach',
  'city',
  'nature',
  'local',
  'heritage',
];

const destinationCategories = [
  { value: 'city', label: 'City' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'food_cafe', label: 'Food & Cafe' },
  { value: 'heritage', label: 'Heritage' },
  { value: 'local', label: 'Local' },
  { value: 'nature', label: 'Nature' },
];

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [form, setForm] = useState(initialForm);
  const [galleryImageUrl, setGalleryImageUrl] = useState('');
  const [galleryImageUrls, setGalleryImageUrls] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postGalleryImages, setPostGalleryImages] = useState([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [isLoadingSubscribers, setIsLoadingSubscribers] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [destinationForm, setDestinationForm] = useState(initialDestinationForm);
  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [destinationMode, setDestinationMode] = useState('create');
  const [isSubmittingDestination, setIsSubmittingDestination] = useState(false);
  const [isLoadingDestinations, setIsLoadingDestinations] = useState(false);
  const [isPostPickerOpen, setIsPostPickerOpen] = useState(false);
  const [isDestinationPickerOpen, setIsDestinationPickerOpen] = useState(false);

  const subscriberCount = useMemo(() => subscribers.length, [subscribers]);

  useEffect(() => {
    const checkSession = async () => {
      const session = await base44.auth.getSession();
      const email = session?.user?.email?.toLowerCase();
      if (email === ADMIN_EMAIL.toLowerCase()) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    };

    checkSession();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadSubscribers();
    loadBlogPosts();
    loadDestinations();
  }, [isAuthenticated]);

  const loadSubscribers = async () => {
    setIsLoadingSubscribers(true);
    try {
      const data = await base44.entities.Subscriber.list();
      setSubscribers(data || []);
    } catch (error) {
      console.error('Failed to load subscribers', error);
      const detail = error?.message || 'Check your Supabase table policies and anon key permissions.';
      setFeedback({ type: 'error', message: `Unable to load subscribers right now. ${detail}` });
    } finally {
      setIsLoadingSubscribers(false);
    }
  };

  const loadBlogPosts = async () => {
    setIsLoadingPosts(true);
    try {
      const data = await base44.entities.BlogPost.list('-created_date');
      setPosts(data || []);
    } catch (error) {
      console.error('Failed to load posts', error);
      setFeedback({ type: 'error', message: 'Unable to load posts right now.' });
    } finally {
      setIsLoadingPosts(false);
    }
  };

  const loadDestinations = async () => {
    setIsLoadingDestinations(true);
    try {
      const data = await base44.entities.Destination.list();
      setDestinations(data || []);
    } catch (error) {
      console.error('Failed to load destinations', error);
      setFeedback({ type: 'error', message: 'Unable to load destinations right now.' });
    } finally {
      setIsLoadingDestinations(false);
    }
  };

  const loadPostGalleryImages = async (postId) => {
    try {
      const images = await base44.entities.GalleryImage.list(postId);
      setPostGalleryImages(images || []);
    } catch (error) {
      console.error('Failed to load gallery images', error);
    }
  };

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((current) => ({ ...current, [name]: value }));
    setLoginError('');
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const email = loginForm.email.trim().toLowerCase();
    const password = loginForm.password;

    if (!email || !password) {
      setLoginError('Email and password are required.');
      return;
    }

    if (email !== ADMIN_EMAIL.toLowerCase()) {
      setLoginError('Invalid admin account.');
      return;
    }

    setIsAuthenticating(true);
    setLoginError('');
    setFeedback(null);

    try {
      const session = await base44.auth.signIn(email, password);
      if (!session) {
        throw new Error('Authentication failed.');
      }

      setIsAuthenticated(true);
      setLoginForm({ email: '', password: '' });
      setLoginError('');
      setFeedback({ type: 'success', message: 'Successfully signed in.' });
    } catch (error) {
      console.error('Login failed', error);
      setLoginError(error?.message || 'Invalid credentials. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLogout = async () => {
    await base44.auth.signOut();
    setIsAuthenticated(false);
    setSelectedPost(null);
    setSelectedDestination(null);
    setFeedback({ type: 'success', message: 'Signed out successfully.' });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleAddGalleryImage = () => {
    const imageUrl = galleryImageUrl.trim();
    if (!imageUrl) {
      setFeedback({ type: 'error', message: 'Enter a gallery image URL before adding.' });
      return;
    }
    setGalleryImageUrls((current) => [...current, imageUrl]);
    setGalleryImageUrl('');
  };

  const removeGalleryUrl = (index) => {
    setGalleryImageUrls((current) => current.filter((_, idx) => idx !== index));
  };

  const clearSelectedPost = () => {
    setSelectedPost(null);
    setForm(initialForm);
    setPostGalleryImages([]);
    setGalleryImageUrls([]);
  };

  const handleSelectPost = async (post) => {
    setSelectedPost(post);
    setForm({
      title: post.title || '',
      destination: post.destination || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      image: post.image || '',
      author: post.author || '',
      category: post.category || 'travel',
    });
    setGalleryImageUrls([]);
    await loadPostGalleryImages(post.id);
    setIsPostPickerOpen(false);
  };

  const publishGalleryImages = async (postId) => {
    if (!galleryImageUrls.length) return;
    try {
      await Promise.all(
        galleryImageUrls.map((image_url) =>
          base44.entities.GalleryImage.create({
            post_id: postId,
            image_url,
          })
        )
      );
      setGalleryImageUrls([]);
    } catch (error) {
      console.error('Failed to publish gallery images', error);
    }
  };

  const handleOpenPostPicker = () => setIsPostPickerOpen(true);
  const handleClosePostPicker = () => setIsPostPickerOpen(false);
  const handleOpenDestinationPicker = () => setIsDestinationPickerOpen(true);
  const handleCloseDestinationPicker = () => setIsDestinationPickerOpen(false);

  const handleSubmitStory = async (event) => {
    event.preventDefault();
    setIsPublishing(true);
    setFeedback(null);

    try {
      const payload = buildBlogPostPayload({
        ...form,
        created_date: new Date().toISOString(),
      });

      const post = selectedPost
        ? await base44.entities.BlogPost.update(selectedPost.id, payload)
        : await base44.entities.BlogPost.create(payload);

      if (!post) {
        throw new Error('Story could not be saved.');
      }

      await publishGalleryImages(post.id);

      const notificationResult = selectedPost
        ? null
        : await base44.entities.BlogPost.notify(post).catch((notifyError) => {
            console.warn('Notification failed:', notifyError);
            return null;
          });

      const actionMessage = selectedPost ? 'updated' : 'published';
      const notifyMessage = notificationResult?.success
        ? ` and notified ${notificationResult.sent || 0} subscriber(s)`
        : '';

      setFeedback({ type: 'success', message: `Story ${actionMessage} successfully${notifyMessage}.` });
      await loadBlogPosts();
      setSelectedPost(post);
      await loadPostGalleryImages(post.id);
      setIsPostPickerOpen(false);

      if (!selectedPost) {
        setForm(initialForm);
      }
    } catch (error) {
      console.error('Save failed', error);
      const detail = error?.message || 'Check your Supabase table policies and anon key permissions.';
      setFeedback({ type: 'error', message: `Unable to save story. ${detail}` });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDestinationChange = (event) => {
    const { name, value } = event.target;
    setDestinationForm((current) => ({ ...current, [name]: value }));
  };

  const clearSelectedDestination = () => {
    setSelectedDestination(null);
    setDestinationForm(initialDestinationForm);
  };

  const handleSelectDestination = (destination) => {
    setDestinationMode('edit');
    setSelectedDestination(destination);
    setDestinationForm({
      name: destination.name || '',
      country: destination.country || '',
      description: destination.description || '',
      content: destination.content || '',
      image: destination.image || '',
      category: destination.category || 'city',
      best_season: destination.best_season || '',
      highlights: Array.isArray(destination.highlights) ? destination.highlights.join('\n') : (destination.highlights || ''),
    });
    setIsDestinationPickerOpen(false);
  };

  const handleSubmitDestination = async (event) => {
    event.preventDefault();
    setIsSubmittingDestination(true);
    setFeedback(null);

    try {
      const payload = {
        ...destinationForm,
        highlights: destinationForm.highlights
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean),
        created_date: new Date().toISOString(),
      };

      const destination = destinationMode === 'edit' && selectedDestination
        ? await base44.entities.Destination.update(selectedDestination.id, payload)
        : await base44.entities.Destination.create(payload);

      if (!destination) {
        throw new Error('Destination could not be saved.');
      }

      const message = destinationMode === 'edit'
        ? 'Destination updated successfully.'
        : 'Destination published successfully.';

      setFeedback({ type: 'success', message });
      await loadDestinations();
      if (destinationMode === 'create') {
        clearSelectedDestination();
      } else {
        setSelectedDestination(destination);
      }
    } catch (error) {
      console.error('Destination save failed', error);
      const detail = error?.message || 'Check your Supabase table policies and anon key permissions.';
      setFeedback({ type: 'error', message: `Unable to save destination. ${detail}` });
    } finally {
      setIsSubmittingDestination(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#faf9f7] px-4 py-24 text-[#1a1a2e] md:px-8">
        <div className="mx-auto max-w-md rounded-3xl border border-[#1a1a2e]/10 bg-white p-8 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#c17f59]">Admin login</p>
          <h1 className="mt-4 text-3xl font-semibold text-[#1a1a2e]">Secure admin access</h1>
          <p className="mt-3 text-sm text-[#1a1a2e]/70">
            Enter your Supabase admin email and password to access the dashboard.
          </p>

          {loginError && (
            <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="mt-8 space-y-5">
            <label className="block text-sm font-medium text-[#1a1a2e]">
              Email
              <input
                type="email"
                name="email"
                value={loginForm.email}
                onChange={handleLoginChange}
                className="mt-2 w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                placeholder="admin@offroute.com"
              />
            </label>
            <label className="block text-sm font-medium text-[#1a1a2e]">
              Password
              <input
                type="password"
                name="password"
                value={loginForm.password}
                onChange={handleLoginChange}
                className="mt-2 w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                placeholder="••••••••"
              />
            </label>
            <button
              type="submit"
              disabled={isAuthenticating}
              className="w-full rounded-full bg-[#c17f59] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#a66b48] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isAuthenticating ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f7] px-4 py-24 text-[#1a1a2e] md:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#c17f59]">Admin dashboard</p>
            <h1 className="text-4xl font-semibold text-[#1a1a2e] md:text-5xl">Manage stories, destinations, and gallery assets</h1>
            <p className="max-w-3xl text-lg text-[#1a1a2e]/70">
              Use the secure admin section to create or edit published stories and destinations.
            </p>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="rounded-full border border-[#1a1a2e]/10 bg-white px-5 py-3 text-sm font-semibold text-[#1a1a2e] transition hover:border-[#c17f59] hover:text-[#c17f59]"
          >
            Sign out
          </button>
        </div>

        {feedback && (
          <div className={`rounded-2xl border px-4 py-3 text-sm ${feedback.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
            {feedback.message}
          </div>
        )}

        <div className="grid gap-8 xl:grid-cols-[1.4fr_0.95fr]">
          <section className="rounded-3xl border border-[#1a1a2e]/10 bg-white p-6 shadow-sm md:p-8">
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold">Blog story editor</h2>
                <p className="text-sm text-[#1a1a2e]/60">Publish new stories, choose categories, and add gallery images.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                {selectedPost && (
                  <button
                    type="button"
                    onClick={clearSelectedPost}
                    className="rounded-full border border-[#1a1a2e]/10 px-4 py-2 text-sm font-semibold text-[#1a1a2e] transition hover:bg-[#faf9f7]"
                  >
                    Clear selection
                  </button>
                )}
                <button
                  type="button"
                  onClick={loadBlogPosts}
                  className="rounded-full bg-[#1a1a2e] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#2f3650]"
                >
                  Refresh stories
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitStory} className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                <span>Title</span>
                <input
                  required
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                  placeholder="A hidden gem in Kyoto"
                />
              </label>

              <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                <span>Destination</span>
                <input
                  required
                  name="destination"
                  value={form.destination}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                  placeholder="Kyoto, Japan"
                />
              </label>

              <label className="space-y-2 text-sm font-medium text-[#1a1a2e] md:col-span-2">
                <span>Excerpt</span>
                <textarea
                  required
                  name="excerpt"
                  rows="3"
                  value={form.excerpt}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                  placeholder="A short intro that will appear on cards and newsletters."
                />
              </label>

              <label className="space-y-2 text-sm font-medium text-[#1a1a2e] md:col-span-2">
                <span>Content</span>
                <textarea
                  required
                  name="content"
                  rows="8"
                  value={form.content}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                  placeholder="Write the full story here..."
                />
              </label>

              <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                <span>Cover image</span>
                <input
                  name="image"
                  value={form.image}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                  placeholder="https://example.com/image.jpg"
                />
              </label>

              <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                <span>Category</span>
                <select
                  required
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                >
                  {blogCategories.map((category) => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                <span>Author</span>
                <input
                  required
                  name="author"
                  value={form.author}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                  placeholder="Your name"
                />
              </label>

              <div className="md:col-span-2 grid gap-4 md:grid-cols-[1fr_auto] items-end">
                <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                  <span>Gallery image URL</span>
                  <input
                    value={galleryImageUrl}
                    onChange={(event) => setGalleryImageUrl(event.target.value)}
                    className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                    placeholder="https://example.com/gallery.jpg"
                  />
                </label>
                <button
                  type="button"
                  onClick={handleAddGalleryImage}
                  className="rounded-full bg-[#1a1a2e] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#2f3650]"
                >
                  Add image
                </button>
              </div>

              <div className="md:col-span-2 space-y-3">
                <div className="rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-4">
                  <h3 className="text-sm font-semibold text-[#1a1a2e]">Pending gallery images</h3>
                  {galleryImageUrls.length === 0 ? (
                    <p className="text-sm text-[#1a1a2e]/60">No images queued yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {galleryImageUrls.map((url, index) => (
                        <li key={`${url}-${index}`} className="flex items-center justify-between rounded-2xl bg-white px-3 py-2 text-sm">
                          <span className="truncate">{url}</span>
                          <button
                            type="button"
                            onClick={() => removeGalleryUrl(index)}
                            className="text-xs font-semibold text-[#c17f59] hover:underline"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {postGalleryImages.length > 0 && (
                  <div className="rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-4">
                    <h3 className="text-sm font-semibold text-[#1a1a2e]">Existing gallery images</h3>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {postGalleryImages.map((image) => (
                        <img
                          key={image.id}
                          src={image.image_url}
                          alt="Gallery preview"
                          className="h-32 w-full rounded-3xl object-cover"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="md:col-span-2 flex justify-between gap-4 pt-2">
                <button
                  type="button"
                  onClick={handleOpenPostPicker}
                  className="rounded-full border border-[#1a1a2e]/10 bg-white px-5 py-3 text-sm font-semibold text-[#1a1a2e] hover:bg-[#faf9f7]"
                >
                  Choose post to edit
                </button>
                <button
                  type="button"
                  onClick={clearSelectedPost}
                  className="rounded-full border border-[#1a1a2e]/10 bg-white px-5 py-3 text-sm font-semibold text-[#1a1a2e] hover:bg-[#faf9f7]"
                >
                  Clear fields
                </button>
                <button
                  type="submit"
                  disabled={isPublishing}
                  className="rounded-full bg-[#c17f59] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#a66b48] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {selectedPost ? (isPublishing ? 'Updating...' : 'Update story') : (isPublishing ? 'Publishing...' : 'Publish story')}
                </button>
              </div>
            </form>
          </section>

          {isPostPickerOpen && (
            <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-12">
              <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white p-5 shadow-2xl sm:p-8">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-semibold">Select a story to edit</h3>
                    <p className="text-sm text-[#1a1a2e]/70">Click on a post to load it into the editor.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleClosePostPicker}
                    className="rounded-full border border-[#1a1a2e]/10 bg-white px-4 py-2 text-sm font-semibold text-[#1a1a2e] hover:bg-[#faf9f7]"
                  >
                    Close
                  </button>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {isLoadingPosts ? (
                    <div className="rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-6 text-center text-sm text-[#1a1a2e]/70">Loading stories...</div>
                  ) : posts.length === 0 ? (
                    <div className="rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-6 text-center text-sm text-[#1a1a2e]/70">No stories available to edit.</div>
                  ) : (
                    posts.map((post) => (
                      <button
                        key={post.id}
                        type="button"
                        onClick={() => handleSelectPost(post)}
                        className="rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-5 text-left transition hover:border-[#c17f59] hover:bg-white"
                      >
                        <p className="text-sm font-semibold text-[#1a1a2e]">{post.title || 'Untitled story'}</p>
                        <p className="mt-2 text-sm text-[#1a1a2e]/70 truncate">{post.destination || 'No destination selected'}</p>
                        <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#c17f59]">{post.category || 'travel'}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          <section className="space-y-6">
            <div className="rounded-3xl border border-[#1a1a2e]/10 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium uppercase tracking-[0.25em] text-[#c17f59]">Subscribers</p>
                  <h2 className="text-2xl font-semibold">Subscriber count</h2>
                </div>
                <span className="rounded-full bg-[#faf9f7] px-3 py-1 text-sm font-medium text-[#1a1a2e]">
                  {isLoadingSubscribers ? 'Loading...' : `${subscriberCount} subscribers`}
                </span>
              </div>

              <div className="mt-5 rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-4">
                <p className="text-sm text-[#1a1a2e]/70">
                  Subscriber emails are hidden for privacy. Only the total subscriber count is displayed here.
                </p>
              </div>
            </div>

            <div className="rounded-3xl border border-[#1a1a2e]/10 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium uppercase tracking-[0.25em] text-[#c17f59]">Destinations</p>
                  <h2 className="text-2xl font-semibold">Publish or edit destinations</h2>
                </div>
                <div className="rounded-full bg-[#faf9f7] px-3 py-1 text-sm font-medium text-[#1a1a2e]">
                  {isLoadingDestinations ? 'Loading...' : `${destinations.length} destinations`}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setDestinationMode('create');
                    clearSelectedDestination();
                  }}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${destinationMode === 'create' ? 'bg-[#c17f59] text-white' : 'bg-[#faf9f7] text-[#1a1a2e]'}`}
                >
                  Publish new destination
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDestinationMode('edit');
                    handleOpenDestinationPicker();
                  }}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${destinationMode === 'edit' ? 'bg-[#c17f59] text-white' : 'bg-[#faf9f7] text-[#1a1a2e]'}`}
                >
                  Edit existing destination
                </button>
              </div>

              <form onSubmit={handleSubmitDestination} className="mt-6 grid gap-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                    <span>Name</span>
                    <input
                      required
                      name="name"
                      value={destinationForm.name}
                      onChange={handleDestinationChange}
                      className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                      placeholder="Paris, France"
                    />
                  </label>

                  <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                    <span>Country</span>
                    <input
                      required
                      name="country"
                      value={destinationForm.country}
                      onChange={handleDestinationChange}
                      className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                      placeholder="France"
                    />
                  </label>
                </div>

                <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                  <span>Description</span>
                  <textarea
                    required
                    name="description"
                    rows="3"
                    value={destinationForm.description}
                    onChange={handleDestinationChange}
                    className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                    placeholder="A short description for the destination list."
                  />
                </label>

                <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                  <span>Image URL</span>
                  <input
                    name="image"
                    value={destinationForm.image}
                    onChange={handleDestinationChange}
                    className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                    placeholder="https://example.com/destination.jpg"
                  />
                </label>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                    <span>Category</span>
                    <select
                      required
                      name="category"
                      value={destinationForm.category}
                      onChange={handleDestinationChange}
                      className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                    >
                      {destinationCategories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                    <span>Best season</span>
                    <input
                      name="best_season"
                      value={destinationForm.best_season}
                      onChange={handleDestinationChange}
                      className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                      placeholder="Spring, Summer, Fall..."
                    />
                  </label>
                </div>

                <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                  <span>Key highlights</span>
                  <textarea
                    name="highlights"
                    rows="4"
                    value={destinationForm.highlights}
                    onChange={handleDestinationChange}
                    className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                    placeholder="One highlight per line"
                  />
                  <p className="text-xs text-[#1a1a2e]/60">Separate each highlight on its own line.</p>
                </label>

                <label className="space-y-2 text-sm font-medium text-[#1a1a2e]">
                  <span>Content</span>
                  <textarea
                    required
                    name="content"
                    rows="5"
                    value={destinationForm.content}
                    onChange={handleDestinationChange}
                    className="w-full rounded-2xl border border-[#1a1a2e]/10 bg-[#faf9f7] px-4 py-3 outline-none ring-0"
                    placeholder="Full destination details for the destination page."
                  />
                </label>

                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmittingDestination}
                    className="rounded-full bg-[#c17f59] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#a66b48] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {destinationMode === 'edit' ? (isSubmittingDestination ? 'Updating...' : 'Update destination') : (isSubmittingDestination ? 'Publish...' : 'Publish destination')}
                  </button>
                  <button
                    type="button"
                    onClick={clearSelectedDestination}
                    className="rounded-full border border-[#1a1a2e]/10 bg-white px-5 py-3 text-sm font-semibold text-[#1a1a2e] hover:bg-[#faf9f7]"
                  >
                    Reset form
                  </button>
                </div>
              </form>

              {isDestinationPickerOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-12">
                  <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white p-5 shadow-2xl sm:p-8">
                    <div className="mb-4 flex items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-semibold">Select a destination to edit</h3>
                        <p className="text-sm text-[#1a1a2e]/70">Click on a destination to load it into the form.</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleCloseDestinationPicker}
                        className="rounded-full border border-[#1a1a2e]/10 bg-white px-4 py-2 text-sm font-semibold text-[#1a1a2e] hover:bg-[#faf9f7]"
                      >
                        Close
                      </button>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {isLoadingDestinations ? (
                        <div className="rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-6 text-center text-sm text-[#1a1a2e]/70">Loading destinations...</div>
                      ) : destinations.length === 0 ? (
                        <div className="rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-6 text-center text-sm text-[#1a1a2e]/70">No destinations available to edit.</div>
                      ) : (
                        destinations.map((destination) => (
                          <button
                            key={destination.id}
                            type="button"
                            onClick={() => handleSelectDestination(destination)}
                            className="rounded-3xl border border-[#1a1a2e]/10 bg-[#faf9f7] p-5 text-left transition hover:border-[#c17f59] hover:bg-white"
                          >
                            <p className="text-sm font-semibold text-[#1a1a2e]">{destination.name || 'Untitled destination'}</p>
                            <p className="mt-2 text-sm text-[#1a1a2e]/70 truncate">{destination.country || 'No country set'}</p>
                            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#c17f59]">{destination.category || 'city'}</p>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
